import styled, { css, keyframes } from 'styled-components'
import { accents, gray, semantic } from 'sutra/tokens/colors'
import { motion } from 'sutra/tokens/motion'
import { radii } from 'sutra/tokens/radii'
import { spacing } from 'sutra/tokens/spacing'

import { PHRASE_FADE_MS } from 'features/ask-yogi/features/conversation/hooks/use-cycling-loading-phrase'
import type { RunStatus } from 'features/ask-yogi/types'

const shimmer = keyframes`
  0% { opacity: 0.55; }
  50% { opacity: 1; }
  100% { opacity: 0.55; }
`

// Chrome-free by design: no border, no background, no padding, no radius. The
// trail reads as an inline note above the answer, not as a widget. Card
// remains as a semantic wrapper for max-width, spacing, and the Poppins
// anchor (buttons reset font-family per browser).
export const Card = styled.div<{ $status: RunStatus }>`
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0;
  font-family: 'Poppins', system-ui, sans-serif;

  button {
    font-family: inherit;
  }
`

export const Header = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing[2]}px;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  color: ${semantic.fg};
`

const sparkleColor: Record<RunStatus, string> = {
  in_progress: gray[500],
  completed: gray[500],
  failed: accents.crimsonBloom[500],
  cancelled: gray[400],
}

// Sparkle color signals status (navy default, crimson on failure, muted on
// cancel). The one affordance the removed border was carrying is now on the
// icon itself — no extra chrome introduced.
export const SparkleWrap = styled.span<{
  $working: boolean
  $status: RunStatus
}>`
  display: inline-flex;
  color: ${({ $status }) => sparkleColor[$status]};

  & svg {
    width: 14px;
    height: 14px;
  }

  ${({ $working }) =>
    $working &&
    css`
      animation: ${shimmer} 1.6s ease-in-out infinite;
    `}
`

// Monochrome shimmer: gray→dark-gray→gray sweep, no navy contrast. Quiet
// confidence over "look at me." The shimmer is still the streaming signal,
// but it doesn't fight the surrounding conversation for attention.
const thinkingShimmer = keyframes`
  0%   { background-position: 200% 0; }
  100% { background-position: 0% 0; }
`

export const HeaderLabel = styled.span<{
  $working: boolean
  $visible?: boolean
}>`
  font-size: 13px;
  line-height: 20px;
  font-weight: 400;
  opacity: ${({ $visible }) => ($visible === false ? 0 : 1)};
  transition: opacity ${PHRASE_FADE_MS}ms ease;

  ${({ $working }) =>
    $working
      ? css`
          background: linear-gradient(
            90deg,
            ${gray[400]} 0%,
            ${gray[400]} 35%,
            ${gray[700]} 50%,
            ${gray[400]} 65%,
            ${gray[400]} 100%
          );
          background-size: 200% 100%;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
          animation: ${thinkingShimmer} 2.4s linear infinite;
        `
      : css`
          color: ${gray[500]};
        `}
`

export const Chevron = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  color: ${gray[500]};
  transition: transform ${motion.duration.fast}ms ${motion.ease};
  transform: rotate(${({ $expanded }) => ($expanded ? '180deg' : '0deg')});

  & svg {
    width: 14px;
    height: 14px;
  }
`

export const Body = styled.div`
  margin-top: ${spacing[2]}px;
`

const barPulse = keyframes`
  0%, 100% { opacity: 0.25; }
  50%      { opacity: 1; }
`

export const WarmupRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]}px;
  padding: ${spacing[2]}px 0;
`

export const WarmupBar = styled.div<{ $delay: number }>`
  height: 8px;
  border-radius: ${radii.xs}px;
  background: ${gray[100]};
  animation: ${barPulse} 1.4s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay}ms;

  &:nth-child(1) {
    width: 60%;
  }
  &:nth-child(2) {
    width: 45%;
  }
  &:nth-child(3) {
    width: 30%;
  }
`

export const Terminal = styled.div`
  padding-top: ${spacing[2]}px;
`

export const TerminalText = styled.span`
  font-size: 13px;
  color: ${gray[600]};
`
